import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

interface ToastProps {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ id, type, title, message, duration = 5000, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const showTimer = setTimeout(() => setIsVisible(true), 10);

    // Auto-dismiss after duration
    const dismissTimer = duration > 0 ? setTimeout(() => {
      handleClose();
    }, duration) : null;

    return () => {
      clearTimeout(showTimer);
      if (dismissTimer) clearTimeout(dismissTimer);
    };
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose(id);
    }, 300); // Match animation duration
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <AlertCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'info':
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getStyles = () => {
    const baseStyles = "relative overflow-hidden shadow-lg rounded-lg p-4 mb-4 max-w-md transition-all duration-300 transform";
    const visibilityStyles = isVisible && !isLeaving ? "translate-x-0 opacity-100" : "translate-x-full opacity-0";
    
    let colorStyles = "";
    switch (type) {
      case 'success':
        colorStyles = "bg-green-50 border-l-4 border-green-500 text-green-800";
        break;
      case 'error':
        colorStyles = "bg-red-50 border-l-4 border-red-500 text-red-800";
        break;
      case 'warning':
        colorStyles = "bg-yellow-50 border-l-4 border-yellow-500 text-yellow-800";
        break;
      case 'info':
      default:
        colorStyles = "bg-blue-50 border-l-4 border-blue-500 text-blue-800";
        break;
    }

    return `${baseStyles} ${colorStyles} ${visibilityStyles}`;
  };

  const getIconColor = () => {
    switch (type) {
      case 'success':
        return "text-green-600";
      case 'error':
        return "text-red-600";
      case 'warning':
        return "text-yellow-600";
      case 'info':
      default:
        return "text-blue-600";
    }
  };

  return (
    <div className={getStyles()}>
      <div className="flex items-start">
        <div className={`flex-shrink-0 ${getIconColor()}`}>
          {getIcon()}
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium">
            {title}
          </h3>
          <p className="mt-1 text-sm">
            {message}
          </p>
        </div>
        <button
          onClick={handleClose}
          className="ml-4 flex-shrink-0 inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Progress bar for auto-dismiss */}
      {duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
          <div
            className={`h-full ${
              type === 'success' ? 'bg-green-500' :
              type === 'error' ? 'bg-red-500' :
              type === 'warning' ? 'bg-yellow-500' :
              'bg-blue-500'
            } transition-all duration-[${duration}ms] ease-linear`}
            style={{
              animation: `shrink ${duration}ms linear`,
              width: '100%'
            }}
          />
        </div>
      )}
      
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default Toast;
